import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import { config } from '../config'
import { useListPublicationsQuery } from '../api/authoring'
import { problemMessage } from '../api/baseApi'

/**
 * The site, in the screen.
 *
 * **It is delivery, not a second composer.** The frame loads the very
 * page a visitor loads, rendered by the site's own front-end from the
 * same read a front-end calls. The parity work exists precisely to avoid
 * maintaining two composers, and a preview that drew the page itself
 * would be the second one.
 *
 * **It shows the release, and says so.** Delivery serves the Website's
 * current publication. An authored edit is intentionally absent until a
 * new release captures it, so refreshing is useful for a release that has
 * just reached the site and is not a preview of unpublished work.
 *
 * **It is cross-origin, so it is a picture.** The admin cannot script
 * into a customer's site: there is no click-to-select, no
 * scroll-to-the-block-you-just-edited, no highlight, and there will not
 * be one from here. That is the visual designer, it is a separate piece
 * of work, and this frame is not its first half however much it looks
 * like one.
 *
 * It also depends on something outside this client: the site's own
 * front-end has to permit framing from the admin's origin. A site that
 * refuses shows an empty frame with no error a script can catch, which
 * is why the way out — open it in a tab — is drawn beside it rather than
 * offered after a failure nothing can detect.
 *
 * **Width is the whole argument for `size`.** A frame is a rendering of
 * a real page by the real front-end, so it answers the site's own
 * breakpoints — and a frame given half a screen answers the phone ones.
 * An author checking a desktop homepage in a 560px column is being shown
 * a truthful rendering of the wrong page, which is worse than no frame
 * at all. `screen` is the frame with a face to itself, and it takes the
 * height of one as well as the width.
 */
export function SitePreview({
  tenant,
  site,
  host,
  path,
  size = 'panel',
}: {
  tenant: string
  site: string
  host?: string
  path?: string
  size?: 'panel' | 'screen'
}) {
  // Bumped on refresh and carried as a query parameter, because
  // reloading a cross-origin frame is not something this document is
  // allowed to do: it can only point the frame at a different URL, and a
  // changed parameter is what makes it one.
  const [attempt, setAttempt] = useState(0)
  const releases = useListPublicationsQuery({ tenant, website: site })

  // The configured origin **wins**, and only the local loop configures
  // one. A seeded local tenant binds a hostname like any other — the
  // Bluu site binds `bluu-interactive.local` — so preferring the site's
  // own address would point the frame at something no local machine
  // resolves, which is exactly the preview that only works against a
  // deployed stage. Deployed, this is empty and the site's canonical
  // hostname is the address.
  const origin = config.previewOrigin || (host ? `https://${host}` : '')
  const address = origin ? `${origin}${path && path !== '/' ? path : '/'}` : undefined
  const published = Boolean(releases.data?.current)
  const framed = address && published ? `${address}${address.includes('?') ? '&' : '?'}preview=${attempt}` : undefined

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          Published page
        </Typography>
        {address && published && (
          <>
            <Tooltip title="Reload the live page. A release that just finished may take a moment to appear.">
              <IconButton size="small" onClick={() => setAttempt(attempt + 1)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              endIcon={<OpenInNewIcon />}
              component={MuiLink}
              href={address}
              target="_blank"
              rel="noreferrer"
              sx={{ textTransform: 'none' }}
            >
              Open
            </Button>
          </>
        )}
      </Stack>

      {releases.error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {problemMessage(releases.error, 'Could not check what is live.')}
        </Alert>
      ) : releases.isLoading ? (
        <Alert severity="info" sx={{ m: 2 }}>Checking what is live…</Alert>
      ) : framed ? (
        <Box
          component="iframe"
          key={framed}
          src={framed}
          title="The page as a visitor sees it"
          // No `allow-same-origin`: the frame is another origin's
          // document and this one has no business reaching into it. The
          // sandbox says so explicitly rather than relying on the
          // browser's cross-origin rules to be the only thing stopping
          // it.
          sandbox="allow-scripts allow-popups allow-forms"
          referrerPolicy="no-referrer"
          // Taller where there is room. A 520px window onto a page is
          // about one screenful of a phone, which is not what an owner
          // checking their homepage came to look at — and on its own
          // face it is measured against the window rather than in fixed
          // steps, because everything else on that screen is a heading
          // and a tab strip.
          sx={{
            display: 'block',
            width: '100%',
            height:
              size === 'screen'
                ? { xs: 480, md: 'calc(100vh - 320px)' }
                : { xs: 420, md: 620, xl: 760 },
            minHeight: size === 'screen' ? 480 : undefined,
            border: 0,
            bgcolor: 'background.default',
          }}
        />
      ) : !published ? (
        <Alert severity="warning" sx={{ m: 2 }}>
          This site has not been published yet, so there is nothing to show. Publish it from Releases to see a page
          here.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ m: 2 }}>
          This site has no address yet, so there is nothing to show. Once a hostname is pointed at it, the page appears
          here.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
        This is the live site, so unpublished changes do not appear here. Nothing in the frame can be edited.
      </Typography>
    </Paper>
  )
}
